"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";
import { BattleSprite } from "@/components/game/BattleSprite";
import { PLUGIN_CATALOG, findPluginById, type Plugin } from "@/data/plugins";
import type { PluginLoadout } from "@/engine/plugin-system";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

type PartyMember = {
  speciesId: string;
  nickname?: string;
  level: number;
  loadout: PluginLoadout;
};

type ToastMessage = { ok: boolean; message: string };

interface PluginEquipScreenProps {
  open: boolean;
  party: PartyMember[];
  ownedPluginIds: Record<string, number>;
  onInstall: (
    partyIndex: number,
    pluginId: string,
  ) => Promise<{ ok: boolean; message: string }>;
  onUninstall: (partyIndex: number, pluginId: string) => void;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Style tokens (cream + ink, no neon, no rounded corners — PIXEL_STYLE.md)
// ---------------------------------------------------------------------------

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const DIM = "#8a8377";
const WALNUT = "#5c4a30";
const SAND = "#b89968";
const ALERT = "#c54a4a";
const MOSS = "#7a9a4a";

// ---------------------------------------------------------------------------
// Rarity (data has no rarity field; default everything to "common").
// Hooks reserved for future enrichment.
// ---------------------------------------------------------------------------

type Rarity = "common" | "uncommon" | "rare" | "epic" | "legendary";

const RARITY_COLOR: Record<Rarity, string> = {
  common: "#8a8a8a",
  uncommon: "#7a9a4a",
  rare: "#4a7eb8",
  epic: "#a04ab8",
  legendary: "#c5a83a",
};

function pluginRarity(_plugin: Plugin): Rarity {
  // Until the data layer carries rarity, all plugins read as "common".
  return "common";
}

function pluginIconKey(plugin: Plugin): string {
  return plugin.id;
}

function pluginDescription(plugin: Plugin): string {
  if (plugin.unlocksEvolution) {
    const lvl = plugin.unlocksEvolution.minLevel;
    return lvl
      ? `Unlocks a branching evolution at Lv ${lvl}.`
      : `Unlocks a branching evolution path.`;
  }
  return `${plugin.category[0]?.toUpperCase()}${plugin.category.slice(1)} capability.`;
}

function pluginEffectsSummary(plugin: Plugin): string[] {
  const out: string[] = [`Category: ${plugin.category}`];
  if (plugin.unlocksEvolution) {
    const ev = plugin.unlocksEvolution;
    out.push(
      `Evolves species ${ev.fromSpecies} -> ${ev.toSpecies}${
        ev.minLevel ? ` (Lv ${ev.minLevel}+)` : ""
      }`,
    );
  }
  return out;
}

// ---------------------------------------------------------------------------
// Tabs — driven by real Plugin categories. "ALL" is always first.
// ---------------------------------------------------------------------------

type TabKey = "all" | Plugin["category"];

type TabDef = { key: TabKey; label: string };

const TABS: TabDef[] = [
  { key: "all" as TabKey, label: "ALL" },
  { key: "growth" as TabKey, label: "GROWTH" },
  { key: "combat" as TabKey, label: "COMBAT" },
  { key: "utility" as TabKey, label: "UTILITY" },
  { key: "social" as TabKey, label: "SOCIAL" },
  { key: "economy" as TabKey, label: "ECONOMY" },
];

// ---------------------------------------------------------------------------
// Slot budget — UI convention; engine has no cap today.
// ---------------------------------------------------------------------------

const MAX_SLOTS = 6;

// ---------------------------------------------------------------------------
// Audio
// ---------------------------------------------------------------------------

type SfxName = "menu_move" | "menu_select" | "menu_back";

function playSfx(name: SfxName): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    surggiesAudio?: { playSfx?: (n: string) => void };
  };
  try {
    w.surggiesAudio?.playSfx?.(name);
  } catch {
    // best-effort
  }
}

// ---------------------------------------------------------------------------
// Columns
// ---------------------------------------------------------------------------

type Column = "party" | "loadout" | "available";

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PluginEquipScreen({
  open,
  party,
  ownedPluginIds,
  onInstall,
  onUninstall,
  onClose,
}: PluginEquipScreenProps) {
  const [column, setColumn] = useState<Column>("party");
  const [partyIndex, setPartyIndex] = useState(0);
  const [loadoutIndex, setLoadoutIndex] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);
  const [availIndex, setAvailIndex] = useState(0);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // -----------------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------------

  const focusedMember: PartyMember | undefined = party[partyIndex];
  const equippedIds: string[] = focusedMember?.loadout.equipped ?? [];
  const usedSlots = equippedIds.length;
  const freeSlots = Math.max(0, MAX_SLOTS - usedSlots);

  const activeTab = TABS[tabIndex].key;

  // The full set of plugins visible in the right column:
  // PLUGIN_CATALOG entries that match the active tab.
  const availableList = useMemo<Plugin[]>(() => {
    return PLUGIN_CATALOG.filter((p) =>
      activeTab === "all" ? true : p.category === activeTab,
    );
  }, [activeTab]);

  // Clamp indices when their lists shrink.
  useEffect(() => {
    if (partyIndex > party.length - 1) {
      setPartyIndex(Math.max(0, party.length - 1));
    }
  }, [party.length, partyIndex]);

  useEffect(() => {
    if (loadoutIndex > Math.max(0, equippedIds.length - 1)) {
      setLoadoutIndex(Math.max(0, equippedIds.length - 1));
    }
  }, [equippedIds.length, loadoutIndex]);

  useEffect(() => {
    if (availIndex > Math.max(0, availableList.length - 1)) {
      setAvailIndex(Math.max(0, availableList.length - 1));
    }
  }, [availableList.length, availIndex]);

  // Reset when closed.
  useEffect(() => {
    if (!open) {
      setColumn("party");
      setPartyIndex(0);
      setLoadoutIndex(0);
      setTabIndex(0);
      setAvailIndex(0);
      setToast(null);
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
        toastTimer.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = useCallback((msg: ToastMessage) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setToast(null);
      toastTimer.current = null;
    }, 1500);
  }, []);

  // -----------------------------------------------------------------------
  // Actions
  // -----------------------------------------------------------------------

  const performInstall = useCallback(
    async (memberIdx: number, pluginId: string) => {
      if (busy) return;
      setBusy(true);
      try {
        playSfx("menu_select");
        const result = await onInstall(memberIdx, pluginId);
        showToast(result);
      } catch (err) {
        showToast({
          ok: false,
          message:
            err instanceof Error ? err.message : "Failed to install plugin.",
        });
      } finally {
        setBusy(false);
      }
    },
    [busy, onInstall, showToast],
  );

  const performUninstall = useCallback(
    (memberIdx: number, pluginId: string) => {
      if (busy) return;
      playSfx("menu_back");
      onUninstall(memberIdx, pluginId);
    },
    [busy, onUninstall],
  );

  const canInstall = useCallback(
    (plugin: Plugin): { ok: boolean; reason?: string } => {
      const count = ownedPluginIds[plugin.id] ?? 0;
      if (count <= 0) return { ok: false, reason: "Not owned." };
      if (!focusedMember) return { ok: false, reason: "No party member." };
      if (focusedMember.loadout.equipped.includes(plugin.id)) {
        return { ok: false, reason: "Already installed." };
      }
      if (freeSlots <= 0) return { ok: false, reason: "No free slots." };
      return { ok: true };
    },
    [focusedMember, freeSlots, ownedPluginIds],
  );

  // -----------------------------------------------------------------------
  // Focused plugin (for detail panel)
  // -----------------------------------------------------------------------

  const focusedPlugin: Plugin | undefined = useMemo(() => {
    if (column === "loadout") {
      const id = equippedIds[loadoutIndex];
      return id ? findPluginById(id) : undefined;
    }
    if (column === "available") {
      return availableList[availIndex];
    }
    return undefined;
  }, [column, equippedIds, loadoutIndex, availableList, availIndex]);

  // -----------------------------------------------------------------------
  // Keyboard
  // -----------------------------------------------------------------------

  const cycleColumn = useCallback((dir: 1 | -1) => {
    setColumn((c) => {
      const order: Column[] = ["party", "loadout", "available"];
      const i = order.indexOf(c);
      const next = order[(i + dir + order.length) % order.length];
      if (next !== c) playSfx("menu_move");
      return next;
    });
  }, []);

  const moveWithin = useCallback(
    (dir: -1 | 1) => {
      if (column === "party") {
        if (party.length === 0) return;
        setPartyIndex((i) => {
          const n = Math.max(0, Math.min(party.length - 1, i + dir));
          if (n !== i) playSfx("menu_move");
          return n;
        });
      } else if (column === "loadout") {
        if (equippedIds.length === 0) return;
        setLoadoutIndex((i) => {
          const n = Math.max(0, Math.min(equippedIds.length - 1, i + dir));
          if (n !== i) playSfx("menu_move");
          return n;
        });
      } else {
        if (availableList.length === 0) return;
        setAvailIndex((i) => {
          const n = Math.max(0, Math.min(availableList.length - 1, i + dir));
          if (n !== i) playSfx("menu_move");
          return n;
        });
      }
    },
    [availableList.length, column, equippedIds.length, party.length],
  );

  const cycleTab = useCallback((dir: 1 | -1) => {
    setTabIndex((t) => {
      const n = (t + dir + TABS.length) % TABS.length;
      if (n !== t) playSfx("menu_move");
      return n;
    });
    setAvailIndex(0);
  }, []);

  const confirmFocused = useCallback(() => {
    if (column === "party") {
      // Just focus — already focused. No-op confirm.
      playSfx("menu_select");
      return;
    }
    if (column === "loadout") {
      const id = equippedIds[loadoutIndex];
      if (!id || !focusedMember) return;
      performUninstall(partyIndex, id);
      return;
    }
    // available
    const plugin = availableList[availIndex];
    if (!plugin) return;
    const eligibility = canInstall(plugin);
    if (!eligibility.ok) {
      showToast({ ok: false, message: eligibility.reason ?? "Cannot install." });
      return;
    }
    void performInstall(partyIndex, plugin.id);
  }, [
    availIndex,
    availableList,
    canInstall,
    column,
    equippedIds,
    focusedMember,
    loadoutIndex,
    partyIndex,
    performInstall,
    performUninstall,
    showToast,
  ]);

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      const handled = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Enter",
        "Tab",
        "z",
        "Z",
        "x",
        "X",
        "q",
        "Q",
        "e",
        "E",
        "Escape",
      ];
      if (handled.includes(key)) e.preventDefault();

      switch (key) {
        case "Tab":
          cycleColumn(e.shiftKey ? -1 : 1);
          break;
        case "q":
        case "Q":
          cycleColumn(-1);
          break;
        case "e":
        case "E":
          cycleColumn(1);
          break;
        case "ArrowUp":
          moveWithin(-1);
          break;
        case "ArrowDown":
          moveWithin(1);
          break;
        case "ArrowLeft":
          // In the available column, left/right cycles category tabs.
          if (column === "available") cycleTab(-1);
          else moveWithin(-1);
          break;
        case "ArrowRight":
          if (column === "available") cycleTab(1);
          else moveWithin(1);
          break;
        case "Enter":
        case "z":
        case "Z":
          confirmFocused();
          break;
        case "x":
        case "X":
        case "Escape":
          if (toast) {
            // Dismiss toast first.
            playSfx("menu_back");
            setToast(null);
            if (toastTimer.current) {
              clearTimeout(toastTimer.current);
              toastTimer.current = null;
            }
          } else {
            onClose();
          }
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    column,
    confirmFocused,
    cycleColumn,
    cycleTab,
    moveWithin,
    onClose,
    toast,
  ]);

  if (!open) return null;

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Plugin Loadout"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        backgroundColor: "rgba(26, 26, 26, 0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        imageRendering: "pixelated",
        padding: "1.25rem",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          width: "min(72rem, 100%)",
          maxHeight: "100%",
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          padding: "0.875rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `2px solid ${INK}`,
            paddingBottom: "0.5rem",
          }}
        >
          <PixelText
            as="div"
            glow="none"
            size="lg"
            className="!text-[#1a1a1a]"
          >
            PLUGIN LOADOUT
          </PixelText>
          <button
            type="button"
            aria-label="Close"
            onClick={() => onClose()}
            className="font-mono font-bold uppercase tracking-widest"
            style={{
              backgroundColor: CREAM,
              color: INK,
              border: `2px solid ${INK}`,
              padding: "0.25rem 0.625rem",
              fontSize: "0.85rem",
              lineHeight: 1,
              cursor: "pointer",
              imageRendering: "pixelated",
            }}
          >
            X
          </button>
        </div>

        {/* Body: 3-column grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "16rem 1fr 18rem",
            gap: "0.75rem",
            border: `2px solid ${INK}`,
            backgroundColor: CREAM,
          }}
        >
          {/* Left: party list */}
          <PartyColumn
            party={party}
            partyIndex={partyIndex}
            focused={column === "party"}
            onFocusSlot={(i) => {
              setPartyIndex(i);
              setColumn("party");
              setLoadoutIndex(0);
              playSfx("menu_move");
            }}
            maxSlots={MAX_SLOTS}
          />

          {/* Middle: loadout grid */}
          <LoadoutColumn
            member={focusedMember}
            equippedIds={equippedIds}
            loadoutIndex={loadoutIndex}
            focused={column === "loadout"}
            onSelect={(i) => {
              setLoadoutIndex(i);
              setColumn("loadout");
              playSfx("menu_move");
            }}
            onUninstallSlot={(i) => {
              const id = equippedIds[i];
              if (!id || !focusedMember) return;
              performUninstall(partyIndex, id);
            }}
            maxSlots={MAX_SLOTS}
          />

          {/* Right: available plugins */}
          <AvailableColumn
            tabs={TABS}
            tabIndex={tabIndex}
            setTabIndex={(i) => {
              if (i !== tabIndex) playSfx("menu_move");
              setTabIndex(i);
              setAvailIndex(0);
            }}
            list={availableList}
            availIndex={availIndex}
            focused={column === "available"}
            ownedPluginIds={ownedPluginIds}
            equippedIds={equippedIds}
            onSelect={(i) => {
              setAvailIndex(i);
              setColumn("available");
              playSfx("menu_move");
            }}
            onInstallRow={(i) => {
              const plugin = availableList[i];
              if (!plugin) return;
              const eligibility = canInstall(plugin);
              if (!eligibility.ok) {
                showToast({
                  ok: false,
                  message: eligibility.reason ?? "Cannot install.",
                });
                return;
              }
              void performInstall(partyIndex, plugin.id);
            }}
          />
        </div>

        {/* Detail panel */}
        <DetailPanel plugin={focusedPlugin} />

        {/* Bottom hint strip */}
        <div
          className="font-mono uppercase tracking-widest"
          style={{
            color: INK,
            fontSize: "0.7rem",
            lineHeight: 1,
            borderTop: `2px solid ${INK}`,
            paddingTop: "0.5rem",
          }}
        >
          Z: Confirm &nbsp;|&nbsp; X: Back &nbsp;|&nbsp; Tab/Q/E: Switch Column
          &nbsp;|&nbsp; {"\u2190\u2191\u2192\u2193"}: Navigate
        </div>

        {/* Toast */}
        {toast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              left: "50%",
              bottom: "3rem",
              transform: "translateX(-50%)",
              backgroundColor: CREAM,
              color: INK,
              border: `2px solid ${INK}`,
              borderLeft: `6px solid ${toast.ok ? MOSS : ALERT}`,
              padding: "0.5rem 0.875rem",
              fontSize: "0.8rem",
              fontFamily: "var(--font-mono, ui-monospace)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              imageRendering: "pixelated",
              maxWidth: "32rem",
            }}
          >
            {toast.message}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Party column
// ---------------------------------------------------------------------------

interface PartyColumnProps {
  party: PartyMember[];
  partyIndex: number;
  focused: boolean;
  onFocusSlot: (i: number) => void;
  maxSlots: number;
}

function PartyColumn({
  party,
  partyIndex,
  focused,
  onFocusSlot,
  maxSlots,
}: PartyColumnProps) {
  // Pad to 6 visible slots.
  const slots: (PartyMember | null)[] = [];
  for (let i = 0; i < 6; i++) slots.push(party[i] ?? null);

  return (
    <div
      role="listbox"
      aria-label="Party"
      style={{
        borderRight: `2px solid ${INK}`,
        display: "flex",
        flexDirection: "column",
        maxHeight: "32rem",
        overflowY: "auto",
        outline: focused ? `2px solid ${WALNUT}` : "none",
        outlineOffset: focused ? "-2px" : undefined,
      }}
    >
      <ColumnHeader title="PARTY" active={focused} />
      {slots.map((member, i) => {
        const isActive = i === partyIndex;
        const used = member?.loadout.equipped.length ?? 0;
        return (
          <button
            key={i}
            type="button"
            role="option"
            aria-selected={isActive}
            disabled={!member}
            onMouseEnter={() => {
              if (member) onFocusSlot(i);
            }}
            onClick={() => {
              if (member) onFocusSlot(i);
            }}
            className="font-mono uppercase tracking-widest text-left"
            style={{
              backgroundColor: isActive && member ? INK : CREAM,
              color: isActive && member ? CREAM : INK,
              border: "none",
              borderBottom: `2px solid ${INK}`,
              padding: "0.5rem 0.625rem",
              fontSize: "0.75rem",
              lineHeight: 1.2,
              display: "flex",
              gap: "0.5rem",
              alignItems: "center",
              cursor: member ? "pointer" : "not-allowed",
              imageRendering: "pixelated",
              opacity: member ? 1 : 0.4,
              transition: "none",
            }}
          >
            <div
              style={{
                width: "4rem",
                height: "4rem",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: `2px solid ${isActive && member ? CREAM : INK}`,
                backgroundColor: isActive && member ? WALNUT : "#e7dcc1",
                imageRendering: "pixelated",
                overflow: "hidden",
              }}
              aria-hidden
            >
              {member ? (
                <BattleSprite
                  speciesId={member.speciesId}
                  orientation="front"
                  state="idle"
                  scale={2}
                  battlePortrait
                />
              ) : null}
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.25rem",
                minWidth: 0,
                flex: 1,
              }}
            >
              {member ? (
                <>
                  <span
                    style={{
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {member.nickname || `#${member.speciesId}`}
                  </span>
                  <span style={{ fontSize: "0.65rem", opacity: 0.85 }}>
                    Lv {member.level}
                  </span>
                  <span style={{ fontSize: "0.65rem", opacity: 0.85 }}>
                    {used}/{maxSlots} slots
                  </span>
                </>
              ) : (
                <span style={{ color: DIM, fontSize: "0.7rem" }}>
                  -- Empty --
                </span>
              )}
            </div>
          </button>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Loadout column
// ---------------------------------------------------------------------------

interface LoadoutColumnProps {
  member: PartyMember | undefined;
  equippedIds: string[];
  loadoutIndex: number;
  focused: boolean;
  onSelect: (i: number) => void;
  onUninstallSlot: (i: number) => void;
  maxSlots: number;
}

function LoadoutColumn({
  member,
  equippedIds,
  loadoutIndex,
  focused,
  onSelect,
  onUninstallSlot,
  maxSlots,
}: LoadoutColumnProps) {
  const cells: { id: string | null; index: number }[] = [];
  for (let i = 0; i < maxSlots; i++) {
    cells.push({ id: equippedIds[i] ?? null, index: i });
  }

  return (
    <div
      style={{
        borderRight: `2px solid ${INK}`,
        padding: "0.75rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.625rem",
        outline: focused ? `2px solid ${WALNUT}` : "none",
        outlineOffset: focused ? "-2px" : undefined,
      }}
      aria-label="Loadout"
    >
      <div style={{ marginLeft: "-0.75rem", marginRight: "-0.75rem" }}>
        <ColumnHeader
          title={
            member
              ? `LOADOUT ${equippedIds.length}/${maxSlots}`
              : "LOADOUT"
          }
          active={focused}
        />
      </div>

      {!member ? (
        <div
          className="font-mono uppercase tracking-widest"
          style={{
            color: DIM,
            fontSize: "0.75rem",
            padding: "1rem 0",
          }}
        >
          Select a party member.
        </div>
      ) : (
        <div
          role="listbox"
          aria-label="Installed plugins"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gap: "0.5rem",
          }}
        >
          {cells.map((cell) => {
            const plugin = cell.id ? findPluginById(cell.id) : undefined;
            const isActive =
              focused && cell.id && cell.index === loadoutIndex;
            const filled = !!plugin;
            return (
              <button
                key={cell.index}
                type="button"
                role="option"
                aria-selected={!!isActive}
                disabled={!filled}
                onMouseEnter={() => {
                  if (filled) onSelect(cell.index);
                }}
                onClick={() => {
                  if (filled) onSelect(cell.index);
                }}
                onDoubleClick={() => {
                  if (filled) onUninstallSlot(cell.index);
                }}
                className="font-mono uppercase tracking-widest"
                style={{
                  backgroundColor: filled
                    ? isActive
                      ? INK
                      : CREAM
                    : "#2a2a2a",
                  color: filled ? (isActive ? CREAM : INK) : DIM,
                  border: `2px solid ${INK}`,
                  padding: "0.5rem",
                  minHeight: "6rem",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.375rem",
                  cursor: filled ? "pointer" : "default",
                  imageRendering: "pixelated",
                  transition: "none",
                }}
              >
                {filled ? (
                  <>
                    <PluginIcon plugin={plugin} size={24} />
                    <span
                      style={{
                        fontSize: "0.65rem",
                        lineHeight: 1,
                        textAlign: "center",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        width: "100%",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {plugin.name}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: "0.7rem" }}>EMPTY</span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Available column
// ---------------------------------------------------------------------------

interface AvailableColumnProps {
  tabs: { key: TabKey; label: string }[];
  tabIndex: number;
  setTabIndex: (i: number) => void;
  list: Plugin[];
  availIndex: number;
  focused: boolean;
  ownedPluginIds: Record<string, number>;
  equippedIds: string[];
  onSelect: (i: number) => void;
  onInstallRow: (i: number) => void;
}

function AvailableColumn({
  tabs,
  tabIndex,
  setTabIndex,
  list,
  availIndex,
  focused,
  ownedPluginIds,
  equippedIds,
  onSelect,
  onInstallRow,
}: AvailableColumnProps) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        maxHeight: "32rem",
        outline: focused ? `2px solid ${WALNUT}` : "none",
        outlineOffset: focused ? "-2px" : undefined,
      }}
      aria-label="Available plugins"
    >
      <ColumnHeader title="AVAILABLE" active={focused} />

      {/* Filter tabs */}
      <div
        role="tablist"
        aria-label="Filter"
        style={{
          display: "flex",
          flexWrap: "wrap",
          borderBottom: `2px solid ${INK}`,
        }}
      >
        {tabs.map((tab, i) => {
          const active = i === tabIndex;
          return (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTabIndex(i)}
              className="font-mono font-bold uppercase tracking-widest"
              style={{
                backgroundColor: active ? INK : CREAM,
                color: active ? CREAM : INK,
                border: "none",
                borderRight: `2px solid ${INK}`,
                padding: "0.375rem 0.5rem",
                fontSize: "0.65rem",
                lineHeight: 1,
                cursor: "pointer",
                imageRendering: "pixelated",
                flex: "1 0 auto",
                transition: "none",
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Scrollable list */}
      <div
        role="listbox"
        style={{
          flex: 1,
          overflowY: "auto",
        }}
      >
        {list.length === 0 ? (
          <div
            className="font-mono uppercase tracking-widest"
            style={{
              color: DIM,
              padding: "1rem 0.75rem",
              fontSize: "0.75rem",
            }}
          >
            No plugins in this category.
          </div>
        ) : (
          list.map((plugin, i) => {
            const count = ownedPluginIds[plugin.id] ?? 0;
            const alreadyInstalled = equippedIds.includes(plugin.id);
            const disabled = count <= 0 || alreadyInstalled;
            const isActive = focused && i === availIndex;
            const rarity = pluginRarity(plugin);

            return (
              <button
                key={plugin.id}
                type="button"
                role="option"
                aria-selected={isActive}
                aria-disabled={disabled}
                onMouseEnter={() => onSelect(i)}
                onClick={() => {
                  onSelect(i);
                  if (!disabled) onInstallRow(i);
                }}
                className="font-mono uppercase tracking-widest text-left w-full block relative"
                style={{
                  backgroundColor: isActive ? INK : CREAM,
                  color: isActive ? CREAM : INK,
                  border: "none",
                  borderBottom: `2px solid ${INK}`,
                  padding: "0.5rem 0.625rem",
                  fontSize: "0.75rem",
                  lineHeight: 1.2,
                  display: "flex",
                  alignItems: "center",
                  gap: "0.5rem",
                  cursor: disabled ? "not-allowed" : "pointer",
                  opacity: disabled ? 0.45 : 1,
                  imageRendering: "pixelated",
                  transition: "none",
                  width: "100%",
                }}
              >
                <PluginIcon plugin={plugin} size={24} />
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.2rem",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {plugin.name}
                  </span>
                  <span style={{ fontSize: "0.6rem", opacity: 0.8 }}>
                    {plugin.category}
                    {alreadyInstalled ? " - EQUIPPED" : ""}
                  </span>
                </div>
                <RarityBadge rarity={rarity} active={isActive} />
                <span
                  style={{
                    fontSize: "0.7rem",
                    minWidth: "1.75rem",
                    textAlign: "right",
                  }}
                >
                  x{count}
                </span>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail panel
// ---------------------------------------------------------------------------

function DetailPanel({ plugin }: { plugin: Plugin | undefined }) {
  return (
    <div
      style={{
        border: `2px solid ${INK}`,
        padding: "0.625rem 0.75rem",
        backgroundColor: CREAM,
        minHeight: "5rem",
        display: "flex",
        flexDirection: "column",
        gap: "0.375rem",
      }}
      aria-live="polite"
    >
      {plugin ? (
        <>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              borderBottom: `1px solid ${SAND}`,
              paddingBottom: "0.25rem",
            }}
          >
            <PluginIcon plugin={plugin} size={24} />
            <PixelText
              as="div"
              glow="none"
              size="md"
              className="!text-[#1a1a1a] !tracking-normal !normal-case"
            >
              {plugin.name}
            </PixelText>
            <RarityBadge rarity={pluginRarity(plugin)} active={false} />
          </div>
          <div
            className="font-mono"
            style={{
              color: INK,
              fontSize: "0.78rem",
              lineHeight: 1.4,
            }}
          >
            {pluginDescription(plugin)}
          </div>
          <ul
            className="font-mono uppercase tracking-widest"
            style={{
              color: WALNUT,
              fontSize: "0.65rem",
              lineHeight: 1.3,
              listStyle: "none",
              padding: 0,
              margin: 0,
              display: "flex",
              flexDirection: "column",
              gap: "0.15rem",
            }}
          >
            {pluginEffectsSummary(plugin).map((effect, i) => (
              <li key={i}>- {effect}</li>
            ))}
          </ul>
        </>
      ) : (
        <div
          className="font-mono uppercase tracking-widest"
          style={{
            color: DIM,
            fontSize: "0.75rem",
            lineHeight: 1.2,
          }}
        >
          Highlight a plugin to view its effects.
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-bits
// ---------------------------------------------------------------------------

function ColumnHeader({ title, active }: { title: string; active: boolean }) {
  return (
    <div
      className="font-mono font-bold uppercase tracking-widest"
      style={{
        backgroundColor: active ? INK : WALNUT,
        color: CREAM,
        padding: "0.375rem 0.625rem",
        fontSize: "0.7rem",
        lineHeight: 1,
        borderBottom: `2px solid ${INK}`,
      }}
    >
      {title}
    </div>
  );
}

function RarityBadge({
  rarity,
  active,
}: {
  rarity: Rarity;
  active: boolean;
}) {
  return (
    <span
      className="font-mono font-bold uppercase tracking-widest"
      style={{
        backgroundColor: RARITY_COLOR[rarity],
        color: active ? CREAM : INK,
        padding: "0.1rem 0.35rem",
        fontSize: "0.55rem",
        lineHeight: 1,
        border: `1px solid ${INK}`,
        imageRendering: "pixelated",
      }}
    >
      {rarity}
    </span>
  );
}

function PluginIcon({ plugin, size }: { plugin: Plugin; size: number }) {
  const [errored, setErrored] = useState(false);
  const src = `/assets/surggies/plugins/${pluginIconKey(plugin)}.png`;

  if (errored) {
    return (
      <div
        aria-hidden
        style={{
          width: `${size}px`,
          height: `${size}px`,
          backgroundColor: SAND,
          border: `1px solid ${INK}`,
          imageRendering: "pixelated",
          flexShrink: 0,
        }}
      />
    );
  }

  return (
    <img
      src={src}
      alt=""
      draggable={false}
      width={size}
      height={size}
      onError={() => setErrored(true)}
      style={{
        width: `${size}px`,
        height: `${size}px`,
        imageRendering: "pixelated",
        flexShrink: 0,
      }}
    />
  );
}

export default PluginEquipScreen;
